import React, { useEffect, useState } from "react";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useCallback } from "react";
import useControlPanel from "src/hooks/useControlPanel";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import { dataStateType } from "src/constants/status";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";

import { CACHE_ROGO_DEV_TYPE } from "src/constants/actionCache";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";
import {
  createFLFRogoDevType,
  getFLFRogoDevType,
  removeFLFRogoDevType,
  updateFLFRogoDevType,
} from "src/apis/FLF/rogoDevType.api";
import FLFRogoDevTypeControlPanel from "../../components/ControlPanel/FLFRogoDevTypeControlPanel";
import RogoDevTypeTable from "../../components/Table/RogoDevTypeTable";

const initRogoDevTypeData = {
  list: [],
  total_record: 0,
};

const FLFRogoDevType = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [rogoDevTypeData, setRogoDevTypeData] = useState(initRogoDevTypeData);
  const [filterRogoDevTypes, setFilterRogoDevTypes] = useState([]);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [errorsForm, setErrorsForm] = useState({});

  const fetchRogoDevType = useCallback(() => {
    getFLFRogoDevType({}, (result) => {
      setRogoDevTypeData({ ...result });
      setFilterRogoDevTypes([...result.list]);
    });
  }, []);

  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled) {
      fetchRogoDevType();
    }

    return () => {
      // cancel the request before component unmounts
      isCancelled = true;
    };
  }, [fetchRogoDevType]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  const updateFilteredRogoDevType = useCallback((rogoDevTypes) => {
    setFilterRogoDevTypes(rogoDevTypes);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (rogoDevTypes, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return rogoDevTypes.filter((rogoDevType) =>
        rogoDevType.name.toLowerCase().includes(key.toLowerCase().trim())
      );
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredRogoDevType = value
        ? searchByKeyWord(rogoDevTypeData.list, value)
        : rogoDevTypeData.list;
      updateFilteredRogoDevType(filteredRogoDevType);
    },
    [rogoDevTypeData.list, searchByKeyWord, updateFilteredRogoDevType]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (rogoDevType, resetForm) => {
      if (rogoDevType.id) {
        updateFLFRogoDevType(rogoDevType.id, rogoDevType, handleApiResponse);
      } else {
        createFLFRogoDevType(rogoDevType, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchRogoDevType();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_ROGO_DEV_TYPE);
        }
      }
    },
    [fetchRogoDevType, setCurrentActionCache]
  );

  /* ---- handle sort and DnD ----- */

  const handleDelete = (rogoDevTypeDelete) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        removeFLFRogoDevType(
          rogoDevTypeDelete.id,
          setCurrentActionCache(CACHE_ROGO_DEV_TYPE)
        );
        setRogoDevTypeData((prev) => {
          return {
            ...prev,
            list: prev.list.filter(
              (rogoDevType) => rogoDevType.id !== rogoDevTypeDelete.id
            ),
            total_record: prev.total_record - 1,
          };
        });

        updateFilteredRogoDevType((prev) =>
          prev.filter((rogoDevType) => rogoDevType.id !== rogoDevTypeDelete.id)
        );
      }
    });
  };

  const resultFilter = useMemo(
    () => searchByKeyWord(filterRogoDevTypes, searchKey),
    [filterRogoDevTypes, searchByKeyWord, searchKey]
  );
  const listItems = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>Rogo Dev Type</h2>
      </div>

      {/** control pan */}
      <FLFRogoDevTypeControlPanel
        handleOpenPopup={handleOpenPopup}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFRogoDevTypeControlPanel>

      {/** table */}
      <RogoDevTypeTable
        data={listItems}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
      />

      {/** Pagination */}
      <Pagination
        pathname={path.flfRogoDevType}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFRogoDevType;
